import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { closedDaysLabel, hoursLabel } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { mainNav } from '../../data/navigation';
import { legalDocs } from '../../data/legal';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { isOpen } from '../../core/rules/schedule';
import { ease } from '../../core/format/motion';
import { instagramHandle, lowerFirst } from '../../core/format/text';
import { NewsletterSignup } from './NewsletterSignup';
const colHeading = 'text-[13px] text-shell';
const link = 'underline-offset-4 transition-[text-decoration-color] duration-150 ease-soft hover:underline';

/**
 * De footer, op klei: het donkere slot van elke pagina. Nieuwsbrief groot bovenaan, daaronder
 * vier rustige kolommen (wie, bezoek, tijden, ontdek) en als afsluiting de naam van de salon
 * over de volle breedte, toonsgewijs in het klei: een signatuur, geen extra informatie.
 *
 * Contrast: alle tekst shell op klei (5,6:1). De focusring is hier shell in plaats van klei.
 */
export function Footer() {
  const reduce = useReducedMotion();
  // Gegevens en tijden uit de actuele instellingen (Instellingen › Salon & contact en › Salondagen).
  const {
    salon,
    openingHours
  } = useSite();
  return <footer className="overflow-hidden bg-clay text-shell [&_*:focus-visible]:outline-shell" data-id="element-7764" data-mp="1w90uo3y4up7i">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7765" data-mp="209ujczjeg81g">
        <NewsletterSignup data-id="element-7766" data-mp="20788or17pcf0" />

        <div className="grid grid-cols-2 gap-x-6 gap-y-12 border-t border-shell/25 py-14 lg:grid-cols-12 lg:gap-x-10 lg:py-16" data-id="element-7767" data-mp="1mabdyehz3asu">
          <div className="col-span-2 lg:col-span-4" data-id="element-7768" data-mp="1r7lbyijfgusa">
            <p className="type-title" data-id="element-7769" data-mp="auwff6a48avm">{salon.name}</p>
            <p className="mt-3 max-w-sm text-[15px] leading-relaxed" data-id="element-7770" data-mp="at1vnvakyot4">
              Huidverbetering en 100% vegan skincare in {salon.city}. In de salon behandeld, thuis met de producten die ik zelf gebruik.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[15px]" data-id="element-7771" data-mp="3rytvp7hnoduq">
              {salon.instagram && <li data-id="element-7772" data-mp="3j4p60re9d8gs">
                  <a href={salon.instagram} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7773" data-mp="3j3vznrj7okz6">
                    Instagram <span className="text-shell" data-id="element-7774" data-mp="3e3rswt83v4m5">@{instagramHandle(salon.instagram, salon.name)}</span>
                    <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7775" data-mp="39454roqqo3yk" />
                    <span className="sr-only" data-id="element-7776" data-mp="349rf82yrcr1t"> {shopLabels.newTab}</span>
                  </a>
                </li>}
              <li data-id="element-7777" data-mp="2v79q9grpxkb4">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7778" data-mp="2v7rxf8fx5ixt">
                  WhatsApp
                  <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7779" data-mp="tfn0fijkbvg8" />
                  <span className="sr-only" data-id="element-7780" data-mp="10z61r4wocbdm"> {shopLabels.newTab}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7781" data-mp="15wej53a00bdt">
            <h2 className={colHeading} data-id="element-7782" data-mp="1ao632uynfiuz">
              <Link to="/contact" className={link} data-id="element-7783" data-mp="1aphf1i28zwx6">
                Bezoek & contact
              </Link>
            </h2>
            <address className="mt-4 space-y-3 text-[15px] not-italic leading-relaxed" data-id="element-7784" data-mp="1fpljuatsn37k">
              <p data-id="element-7785" data-mp="i24tnb7tpv5g">
                {salon.street}
                <br data-id="element-7786" data-mp="mybpucaf3sa7" />
                {salon.postalCode} {salon.locality}
                <br data-id="element-7787" data-mp="ryg6c2bbsn0f" />
                <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center underline ${link}`} data-id="element-7788" data-mp="rxycfcc4ykvo">
                  Plan je route<span className="sr-only" data-id="element-7789" data-mp="vxgmte6bzdk9"> {shopLabels.newTab}</span>
                </a>
              </p>
              <p data-id="element-7790" data-mp="27eyh7n20v0u4">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={link} data-id="element-7791" data-mp="27efal2htnyve">
                  <span className="sr-only" data-id="element-7792" data-mp="25xjbei9eet4p">WhatsApp: </span>
                  {salon.phone}
                  <span className="sr-only" data-id="element-7793" data-mp="20yq10yperi8r"> {shopLabels.newTab}</span>
                </a>
                <br data-id="element-7794" data-mp="2fvz4urvweehd" />
                <a href={`mailto:${salon.email}`} className={`break-all ${link}`} data-id="element-7795" data-mp="2fwibvdk0figm">
                  <span className="sr-only" data-id="element-7796" data-mp="2aww1dbvkggbi">E-mail: </span>
                  {salon.email}
                </a>
              </p>
            </address>
            <p className="mt-4 text-[13px] leading-relaxed" data-id="element-7797" data-mp="2ovcebkk0v323">{policyCopy.response}</p>
            {salon.englishNote && <p className="mt-1 text-[13px] leading-relaxed" data-id="element-7798" data-mp="2jx159c1jwwkx">{salon.englishNote}</p>}
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7799" data-mp="2ylxe3bbfmdml">
            <h2 className={colHeading} data-id="element-7800" data-mp="2tn3xvb4rg8iq">Salon, op afspraak</h2>
            <dl className="mt-4 text-[15px]" data-id="element-7801" data-mp="2tpq6le3e0u2j">
              {openingHours.filter(isOpen).map(h => <div key={h.day} className="flex justify-between gap-4 border-b border-shell/20 py-2" data-id="element-7802" data-mp="33ljqjijfrpuc">
                  <dt data-id="element-7803" data-mp="3m9u4ha5vepd1">{h.day}</dt>
                  <dd className="tabular-nums" data-id="element-7804" data-mp="3mawyhqyhp7x6">{hoursLabel(h)}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-[13px]" data-id="element-7805" data-mp="3r8exaoism6rg">{closedDaysLabel(openingHours)}</p>
            <p className="mt-1 text-[13px]" data-id="element-7806" data-mp="3d8461kdkmq5r">De webshop is altijd open: {lowerFirst(policyCopy.delivery)}.</p>
          </div>

          <nav aria-label="Footernavigatie" className="col-span-2 lg:col-span-2" data-id="element-7807" data-mp="3i6febqo7wslv">
            <h2 className={colHeading} data-id="element-7808" data-mp="33iklmaa7mo8s">Ontdek</h2>
            <ul className="mt-2 grid grid-cols-2 text-[15px] lg:mt-3 lg:grid-cols-1" data-id="element-7809" data-mp="38a2mkyc8gotr">
              {mainNav.map(l => <li key={l.to} data-id="element-7810" data-mp="2ujh1d2zb42sp">
                  <Link to={l.to} className={`inline-flex min-h-[44px] items-center lg:min-h-[36px] ${link}`} data-id="element-7811" data-mp="2uk8y4h4n46kk">
                    {l.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>
      </div>

      {/* De signatuur: de naam over de volle breedte, toonsgewijs. Decoratie; de naam staat hierboven al als tekst. */}
      <div aria-hidden="true" className="select-none overflow-hidden" data-id="element-7812" data-mp="v4ecbo9q0xfv">
        <motion.p initial={reduce ? false : {
        y: '35%',
        opacity: 0
      }} whileInView={{
        y: '0%',
        opacity: 1
      }} viewport={{
        once: true,
        margin: '0px 0px -10% 0px'
      }} transition={{
        duration: 0.3,
        ease
      }} className="ornament-wordmark text-center text-shell/[0.14]" data-id="element-7813" data-mp="qcw9svs1uoss">
          {salon.name}
        </motion.p>
      </div>

      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7814" data-mp="1dzw5pvskdaod">
        <div className="flex flex-col gap-3 border-t border-shell/25 py-6 text-[13px] sm:flex-row sm:items-center sm:justify-between" data-id="element-7815" data-mp="1a1yd0izl46g8">
          <p data-id="element-7816" data-mp="153dcsjkc1mrs">
            © {new Date().getFullYear()} {salon.name} · KvK {salon.kvk}
            {salon.vatId && <> · Btw-id {salon.vatId}</>}
          </p>
          {legalDocs.length > 0 && <nav aria-label="Juridisch" data-id="element-7817" data-mp="3ppew5rw16c34">
              <ul className="flex flex-wrap gap-x-5 sm:gap-y-2" data-id="element-7818" data-mp="3ksf10c0pytqs">
                {legalDocs.map(d => <li key={d.path} data-id="element-7819" data-mp="3apsyaas9s0gi">
                    <Link to={d.path} className={`inline-flex min-h-[44px] items-center sm:min-h-0 ${link}`} data-id="element-7820" data-mp="3amn7mr3zsqio">
                      {d.title}
                    </Link>
                  </li>)}
              </ul>
            </nav>}
        </div>
      </div>
    </footer>;
}